import type { CreateInvitesRequest, Invite } from "@bundle/contracts";
import { ApiFailure, Billing, Invites } from "@bundle/core";
import { useState } from "react";
import { Button, Divider, Icon, IconButton, Sheet, Stack, Text, TextField, Toggle } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { useBilling } from "../context/BillingContext.tsx";
import { InviteLimitBanner } from "./billing/BillingBanners.tsx";

export type InvitePeopleSheetProps = {
  open: boolean;
  onClose: () => void;
  workspaceName: string;
  /** Owners and admins may invite admins. */
  canInviteAdmins: boolean;
  /** Creates invites: no emails → one shareable link. */
  onCreate: (req: CreateInvitesRequest) => Promise<Invite[]>;
  /** Copies text to the clipboard (platform). */
  onCopy: (text: string) => Promise<void>;
  /** First run, right after creating the workspace: the close button reads "Skip for now". */
  onboarding?: boolean;
  /** "Contact us" about Pro, from the member-limit prompt (owners and admins). */
  onContact?: () => void;
};

/** "Invite people": a shareable 7-day link and/or invites by email (shown as links when email isn't set up). */
export function InvitePeopleSheet(p: InvitePeopleSheetProps) {
  const t = useT();
  const [link, setLink] = useState<Invite | undefined>(undefined);
  const [linkBusy, setLinkBusy] = useState(false);
  const [copied, setCopied] = useState<string | undefined>(undefined);
  const [text, setText] = useState("");
  const [asAdmins, setAsAdmins] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Invite[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  /** A billing refusal (`billing_required`: read-only workspace; `plan_limit_members`: the plan is full). */
  const [refused, setRefused] = useState<string | undefined>(undefined);
  const parsed = Invites.parseEmailList(text);
  // The plan's member limit (core logic/billing#inviteRoom): pending invitations count, so Send waits until they fit.
  const billing = useBilling();
  const plan = billing?.state.billing;
  const room = plan === undefined ? null : Billing.inviteRoom(plan, parsed.valid.length);
  const onFail = (e: unknown) => {
    if (e instanceof ApiFailure && (e.code === "billing_required" || e.code === "plan_limit_members")) {
      billing?.vm.noteFault(e.code);
      setRefused(e.code);
    } else setFailed(true);
  };

  const copy = (value: string) => {
    void p.onCopy(value).then(() => setCopied(value));
  };

  const createLink = () => {
    if (linkBusy) return;
    setLinkBusy(true);
    setFailed(false);
    setRefused(undefined);
    p.onCreate({ role: "member" }).then(
      (invites) => {
        setLinkBusy(false);
        setLink(invites[0]);
      },
      (e: unknown) => {
        setLinkBusy(false);
        onFail(e);
      },
    );
  };

  const send = () => {
    if (sending || parsed.valid.length === 0 || room !== null) return;
    setSending(true);
    setFailed(false);
    setRefused(undefined);
    p.onCreate({ emails: parsed.valid, role: asAdmins ? "admin" : "member" }).then(
      (invites) => {
        setSending(false);
        setSent(invites);
        setText("");
      },
      (e: unknown) => {
        setSending(false);
        onFail(e);
      },
    );
  };

  const notEmailed = sent === undefined ? [] : sent.filter((i) => !i.emailed);

  return (
    <Sheet open={p.open} onClose={p.onClose} title={t("invite.people.title", { workspace: p.workspaceName })} closeLabel={t("common.close")} testID="invite-people-sheet">
      <Stack gap="5">
        <Stack gap="2">
          <Text variant="headline">{t("invite.people.link")}</Text>
          <Text variant="footnote" color="text.secondary">
            {t("invite.people.linkHint")}
          </Text>
          {link === undefined ? (
            <Stack direction="horizontal" align="center">
              <Button label={t("invite.people.createLink")} icon="link" loading={linkBusy} onPress={createLink} testID="invite-create-link" />
            </Stack>
          ) : (
            <Stack direction="horizontal" gap="2" align="center" padding="2" radius="md" background="bg.surface" border="border.subtle">
              <Stack grow>
                <Text mono selectable variant="footnote" lines={1} testID="invite-link-url">
                  {link.url}
                </Text>
              </Stack>
              <Button
                label={copied === link.url ? t("invite.people.copied") : t("invite.people.copy")}
                icon={copied === link.url ? "check" : "copy"}
                size="sm"
                variant="primary"
                onPress={() => copy(link.url)}
                testID="invite-copy-link"
              />
            </Stack>
          )}
        </Stack>
        <Divider />
        <Stack gap="3">
          <Text variant="headline">{t("invite.people.emails")}</Text>
          <TextField value={text} onChange={setText} placeholder={t("invite.people.emailsPlaceholder")} multiline keyboard="email" testID="invite-emails" />
          {parsed.invalid.length > 0 ? (
            <Text variant="footnote" color="status.danger">
              {t("invite.people.invalid", { list: parsed.invalid.join(", ") })}
            </Text>
          ) : null}
          {parsed.overLimit ? (
            <Text variant="footnote" color="status.warning">
              {t("invite.people.overLimit")}
            </Text>
          ) : null}
          {room === null ? null : (
            <InviteLimitBanner room={room} manage={plan?.canManage === true} workspaceName={p.workspaceName} {...(p.onContact === undefined ? {} : { onContact: p.onContact })} testID="invite-limit" />
          )}
          {p.canInviteAdmins ? <Toggle label={t("invite.people.asAdmins")} value={asAdmins} onChange={setAsAdmins} testID="invite-as-admins" /> : null}
          <Stack direction="horizontal" align="center">
            <Button label={t("invite.people.send")} icon="mail" loading={sending} disabled={parsed.valid.length === 0 || room !== null} onPress={send} testID="invite-send" />
          </Stack>
          {sent === undefined ? null : (
            <Stack gap="2" testID="invite-sent">
              <Stack direction="horizontal" gap="2" align="center">
                <Icon name="checkCircle" size="icon.sm" color="status.success" />
                <Text variant="footnote" color="status.success">
                  {t("invite.people.sent", { count: sent.length })}
                </Text>
              </Stack>
              {notEmailed.length > 0 ? (
                <Stack gap="1">
                  <Text variant="footnote" color="text.secondary">
                    {t("invite.people.notEmailed")}
                  </Text>
                  {notEmailed.map((i) => (
                    <Stack key={i.id} direction="horizontal" gap="2" align="center">
                      <Stack grow>
                        <Text variant="footnote" lines={1}>
                          {i.email ?? i.url}
                        </Text>
                      </Stack>
                      <IconButton
                        icon={copied === i.url ? "check" : "copy"}
                        accessibilityLabel={t("invite.people.copy")}
                        onPress={() => copy(i.url)}
                        testID={`invite-copy-${i.id}`}
                      />
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          )}
        </Stack>
        {refused === "billing_required" ? (
          <Text variant="footnote" color="status.danger" testID="invite-refused">
            {t("billing.fault.required", { ws: p.workspaceName })}
          </Text>
        ) : null}
        {failed ? (
          <Text variant="footnote" color="status.danger">
            {t("common.error")}
          </Text>
        ) : null}
        <Stack direction="horizontal" align="center" justify="end">
          <Button
            label={p.onboarding === true && link === undefined && sent === undefined ? t("invite.people.skip") : t("invite.people.done")}
            variant={p.onboarding === true && link === undefined && sent === undefined ? "ghost" : "primary"}
            onPress={p.onClose}
            testID="invite-done"
          />
        </Stack>
      </Stack>
    </Sheet>
  );
}
